/**
 * EmailVerification page — handle email verification link verification & resend flow.
 * Re-skinned with consentry-v3 design system.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resentSuccess, setResentSuccess] = useState(false);

  useEffect(() => {
    if (!token) return;

    let isMounted = true;
    async function verify() {
      setIsLoading(true);
      setError(null);
      try {
        await apiFetch('/v1/auth/verify-email', {
          method: 'POST',
          body: { token },
        });
        if (isMounted) {
          setIsVerified(true);
          toast.success('Email verified successfully!');
        }
      } catch (err) {
        if (isMounted) {
          const msg = getNetworkErrorMessage(err, 'Verification link invalid or expired.');
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    verify();

    return () => {
      isMounted = false;
    };
  }, [token, toast]);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResentSuccess(false);
    setError(null);
    try {
      await apiFetch('/v1/auth/resend-verification', {
        method: 'POST',
      });
      setResentSuccess(true);
      toast.success('Verification link sent to your email.');
    } catch (err) {
      const msg = getNetworkErrorMessage(err, 'Failed to resend verification link.');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout
      title={token ? 'Verifying Email' : 'Email Verification'}
      subtitle="Verify your email address to unlock full DPDP compliance features"
    >
      {isLoading ? (
        <div className="py-8 text-center space-y-4">
          <Spinner size="lg" className="mx-auto text-blue" />
          <p className="text-sm font-medium text-sub">Verifying your email token...</p>
        </div>
      ) : isVerified ? (
        <div className="space-y-5 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald/10 border border-emerald/20 flex items-center justify-center text-emerald text-2xl font-bold">
            ✓
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-bold text-ink">Email Confirmed!</h3>
            <p className="text-xs text-sub leading-relaxed max-w-xs mx-auto">
              Your email address has been verified. You now have full operational access to your
              consent registers and workspace APIs.
            </p>
          </div>

          <Button
            type="button"
            variant="btn-p"
            fullWidth
            onClick={() => navigate('/dashboard')}
            className="mt-4"
          >
            Enter Dashboard →
          </Button>
        </div>
      ) : error ? (
        <div className="space-y-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-err/10 border border-err/20 flex items-center justify-center text-err text-xl font-bold">
            ✕
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-bold text-ink">Verification Failed</h3>
            <p className="text-xs text-err font-medium bg-err/5 border border-err/20 rounded-xl p-3">
              {error}
            </p>
          </div>

          {resentSuccess ? (
            <div className="rounded-xl border border-ok/20 bg-ok/5 p-3.5 text-xs text-ok font-medium">
              A new verification link has been sent to your email.
            </div>
          ) : (
            <Button
              type="button"
              variant="btn-p"
              fullWidth
              isLoading={isResending}
              loadingText="Sending new link..."
              onClick={handleResendVerification}
            >
              Resend Verification Link
            </Button>
          )}

          <Link
            to="/login"
            className="inline-block text-xs font-semibold text-sub hover:text-ink pt-2"
          >
            Return to sign in
          </Link>
        </div>
      ) : (
        <div className="space-y-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-blue/10 border border-blue/20 flex items-center justify-center text-blue text-xl font-bold">
            ✉
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-bold text-ink">Verification Required</h3>
            <p className="text-xs text-sub leading-relaxed">
              Please check your inbox for a verification email. Click the link in the email to verify
              your account.
            </p>
          </div>

          {resentSuccess ? (
            <div className="rounded-xl border border-ok/20 bg-ok/5 p-3.5 text-xs text-ok font-medium">
              Verification email re-sent! Check your inbox.
            </div>
          ) : (
            <Button
              type="button"
              variant="btn-p"
              fullWidth
              isLoading={isResending}
              loadingText="Sending..."
              onClick={handleResendVerification}
            >
              Resend Verification Email
            </Button>
          )}

          <p className="text-xs text-sub font-medium pt-2">
            Already verified?{' '}
            <Link to="/dashboard" className="text-blue hover:underline font-semibold">
              Go to Dashboard
            </Link>
          </p>
        </div>
      )}
    </AuthLayout>
  );
}

export const EmailVerification = EmailVerificationPage;
